/**
 * Cascading geocoder: Nominatim (primary) → Geoapify (fallback)
 *
 * Nominatim is free/unlimited but misses many Romanian house numbers.
 * Geoapify has OpenAddresses data and finds addresses Nominatim can't.
 * Geoapify free tier: 3,000 req/day — only used when Nominatim fails.
 */

const NOMINATIM_URL = 'https://nominatim.openstreetmap.org/search';
const GEOAPIFY_URL = 'https://api.geoapify.com/v1/geocode/search';
const GEOAPIFY_KEY = import.meta.env.VITE_GEOAPIFY_KEY as string | undefined;

// Arad area viewbox for better local results
const ARAD_VIEWBOX = '21.22,46.10,21.40,46.22';

export interface GeocodeSuggestion {
  id: string;
  displayName: string;
  address: string;
  city: string;
  latitude: number;
  longitude: number;
  source: 'nominatim' | 'geoapify';
  confidence: 'high' | 'medium' | 'low';
}

/* ── Nominatim types ── */
interface NominatimResult {
  place_id: number;
  display_name: string;
  lat: string;
  lon: string;
  type: string;
  class: string;
  address: {
    road?: string;
    house_number?: string;
    neighbourhood?: string;
    suburb?: string;
    city?: string;
    town?: string;
    village?: string;
    county?: string;
    country?: string;
  };
}

/* ── Geoapify types ── */
interface GeoapifyFeature {
  properties: {
    formatted: string;
    housenumber?: string;
    street?: string;
    city?: string;
    town?: string;
    village?: string;
    county?: string;
    lat: number;
    lon: number;
    result_type: string;
    rank: {
      confidence: number;
      match_type: string;
    };
    place_id: string;
  };
}

/**
 * Extract a house number from a user query (e.g. "dambovitei 48a" → "48a")
 */
function extractHouseNumber(query: string): string | null {
  const match = query.match(/\b(\d+\s*[a-zA-Z]?)\s*$/);
  return match?.[1]?.trim() ?? null;
}

function nominatimToSuggestion(r: NominatimResult, userHouseNumber?: string | null): GeocodeSuggestion {
  const addr = r.address;
  const parts: string[] = [];
  if (addr.road) {
    parts.push(addr.road);
    if (addr.house_number) parts.push(addr.house_number);
    else if (userHouseNumber) parts.push(userHouseNumber);
  }
  if (parts.length === 0 && addr.neighbourhood) parts.push(addr.neighbourhood);
  if (parts.length === 0 && addr.suburb) parts.push(addr.suburb);

  // Most specific settlement first: a village inside a commune is the locality
  // people write on the address, not the commune it belongs to.
  const city = addr.village || addr.town || addr.city || addr.county || '';
  const hasHouseNumber = !!addr.house_number;

  return {
    id: `nom-${r.place_id}`,
    displayName: r.display_name,
    address: parts.join(' '),
    city,
    latitude: parseFloat(r.lat),
    longitude: parseFloat(r.lon),
    source: 'nominatim',
    confidence: hasHouseNumber ? 'high' : 'medium',
  };
}

function geoapifyToSuggestion(f: GeoapifyFeature): GeocodeSuggestion {
  const p = f.properties;
  const parts: string[] = [];
  if (p.street) {
    parts.push(p.street);
    if (p.housenumber) parts.push(p.housenumber);
  }

  // Geoapify reports the commune as 'city' and the actual village separately
  // (e.g. city: Livada, village: Sânleani). The village is the locality.
  const city = p.village || p.town || p.city || '';
  const conf = p.rank?.confidence ?? 0;

  return {
    id: `geo-${p.place_id}`,
    displayName: p.formatted,
    address: parts.join(' '),
    city,
    latitude: p.lat,
    longitude: p.lon,
    source: 'geoapify',
    confidence: conf >= 0.7 ? 'high' : conf >= 0.4 ? 'medium' : 'low',
  };
}

/**
 * Check if Nominatim results contain a house-number-level match
 */
function hasHouseNumberMatch(results: NominatimResult[]): boolean {
  return results.some(r => !!r.address?.house_number);
}

/**
 * Query Nominatim
 */
async function queryNominatim(query: string, signal?: AbortSignal): Promise<NominatimResult[]> {
  const params = new URLSearchParams({
    q: query,
    format: 'json',
    addressdetails: '1',
    limit: '6',
    countrycodes: 'ro',
    'accept-language': 'ro',
    viewbox: ARAD_VIEWBOX,
    bounded: '0',
  });

  const res = await fetch(`${NOMINATIM_URL}?${params}`, { signal });
  if (!res.ok) return [];
  return res.json();
}

/**
 * Query Geoapify
 */
async function queryGeoapify(query: string, signal?: AbortSignal): Promise<GeoapifyFeature[]> {
  if (!GEOAPIFY_KEY) return [];

  const params = new URLSearchParams({
    text: query,
    apiKey: GEOAPIFY_KEY,
    lang: 'ro',
    limit: '5',
    filter: 'countrycode:ro',
    bias: 'proximity:21.31,46.18',
  });

  const res = await fetch(`${GEOAPIFY_URL}?${params}`, { signal });
  if (!res.ok) return [];
  const data = await res.json();
  return data.features ?? [];
}

/**
 * Strip Bl/Sc/Et/Ap from geocoding query — these confuse geocoders
 */
function cleanForGeocoding(query: string): string {
  return query
    .replace(/\b(bl\.?|bloc|sc\.?|scara|et\.?|etaj|ap\.?|apartament)\s*[^\s,]*/gi, '')
    .replace(/,\s*,/g, ',')
    .replace(/,\s*$/, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

/**
 * Main cascade: Nominatim first, Geoapify fallback if no house number match.
 * When Nominatim returns street-level results (no house number), the user's
 * typed house number is appended to suggestions so they see a useful address.
 */
export async function geocodeAddress(
  query: string,
  signal?: AbortSignal,
): Promise<GeocodeSuggestion[]> {
  const cleanQuery = cleanForGeocoding(query);
  if (cleanQuery.length < 3) return [];

  const userHouseNumber = extractHouseNumber(cleanQuery);

  // Step 1: Try Nominatim
  let nomResults = await queryNominatim(cleanQuery, signal);

  // If no results, retry with "Arad" appended for better local matching
  if (nomResults.length === 0 && !/arad/i.test(cleanQuery)) {
    nomResults = await queryNominatim(`${cleanQuery}, Arad`, signal);
  }

  // If Nominatim has house-number-level results, use them
  if (hasHouseNumberMatch(nomResults)) {
    return nomResults.map(r => nominatimToSuggestion(r));
  }

  // Step 2: Fallback to Geoapify for better house number coverage
  const geoResults = await queryGeoapify(cleanQuery, signal);

  if (geoResults.length > 0) {
    // Merge: Geoapify results first (better), then Nominatim street-level as fallback
    const geoSuggestions = geoResults.map(geoapifyToSuggestion);
    const nomSuggestions = nomResults.map(r => nominatimToSuggestion(r, userHouseNumber));
    // Dedupe by keeping unique lat/lng (within ~50m)
    const seen = new Set<string>();
    const merged: GeocodeSuggestion[] = [];
    for (const s of [...geoSuggestions, ...nomSuggestions]) {
      const key = `${s.latitude.toFixed(3)},${s.longitude.toFixed(3)}`;
      if (!seen.has(key)) {
        seen.add(key);
        merged.push(s);
      }
    }
    return merged.slice(0, 6);
  }

  // Step 3: Return Nominatim street-level results with user's house number
  return nomResults.map(r => nominatimToSuggestion(r, userHouseNumber));
}
